/**
 * Directorio de GENTE de la academia (F3-D): pestañas Alumnos / Profesores /
 * Personal / Representantes desde los streams admin (org/campus). Búsqueda por
 * nombre/cédula, filtro por sede, e invitación con rol+sede. Todo local-first;
 * la edición del expediente vive en la ruta de detalle (link por fila).
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Search, UserPlus, Users } from "lucide-solid";
import { buildDirectory, type DirectoryRow, hydrateDirectory, type PeopleTab } from "@space/shared";
import { Link } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { PillTabs } from "@/components/ui/pill-tabs";
import { listEntranceProps, useListEntrance } from "@/lib/animation/list-entrance";
import { ExportButton } from "@/features/io/components/export-button";
import { useActiveOrg } from "../../hooks/use-active-org";
import { useCampuses } from "../../hooks/use-academy-data";
import { useMyOrgRole, useOrgDirectory } from "../../hooks/use-people-data";
import { useOrgIdentities } from "../../hooks/use-org-identities";
import { PeopleInviteDialog } from "./people-invite-dialog";

const TABS: { id: PeopleTab; label: MessageDescriptor; inviteRole: string }[] = [
  { id: "students", label: msg`Students`, inviteRole: "student" },
  { id: "teachers", label: msg`Teachers`, inviteRole: "teacher" },
  { id: "staff", label: msg`Staff`, inviteRole: "staff" },
  { id: "guardians", label: msg`Guardians`, inviteRole: "guardian" },
];

export const PeopleDirectoryView: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const campuses = useCampuses(activeOrgId);
  const directory = useOrgDirectory(activeOrgId);
  // D3: la identidad (nombre/imagen/correo) llega por la fuente única. El
  // correo solo viene si el servidor considera que a este actor le corresponde.
  const people = useOrgIdentities(activeOrgId);
  const rbac = useMyOrgRole(activeOrgId);

  const [tab, setTab] = createSignal<PeopleTab>("students");
  const [search, setSearch] = createSignal("");
  const [campusId, setCampusId] = createSignal<string>("");
  const [inviteOpen, setInviteOpen] = createSignal(false);

  const partitioned = createMemo(() =>
    buildDirectory(hydrateDirectory(directory.data ?? [], people.identities()), {
      search: search(),
      campusId: campusId() || null,
    }),
  );

  const rows = () => partitioned()[tab()];
  const countFor = (id: PeopleTab) => partitioned()[id].length;
  const tabItems = createMemo(() => TABS.map((td) => ({ id: td.id, label: t(td.label) })));

  // La pestaña forma parte del ámbito: cambiar de Alumnos a Profesores es un
  // cambio de lista (acción del usuario), no una llegada de datos. Los refetches
  // de PowerSync no lo tocan → la entrada no se repite.
  const isEntering = useListEntrance({
    scope: () => `people:${activeOrgId() ?? ""}:${tab()}`,
    hasItems: () => rows().length > 0,
  });

  const campusOptions = () => [
    { value: "", label: t(msg`All campuses`) },
    ...(campuses.data ?? []).map((c) => ({ value: c.id, label: c.name ?? c.id })),
  ];

  const canInvite = () => rbac.can("ManageMembers");
  const inviteRoleForTab = () => TABS.find((x) => x.id === tab())?.inviteRole ?? "student";

  return (
    <PageContainer maxWidth="4xl">
      <div class="space-y-5">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <PageHeader
            title={t(msg`People`)}
            description={t(msg`Students, teachers, staff and guardians of your academy.`)}
            icon={Users}
          />
          <div class="flex items-center gap-2">
            {/* F4-B: export del directorio de alumnos (io.students) con el filtro de sede. */}
            <ExportButton
              entityKey="io.students"
              orgId={activeOrgId()}
              filters={{ campus_id: campusId() || undefined }}
            />
            <Show when={canInvite()}>
              <Button
                class="gap-2"
                data-testid="people-invite-open"
                onClick={() => setInviteOpen(true)}
              >
                <UserPlus class="h-4 w-4" /> {t(msg`Invite`)}
              </Button>
            </Show>
          </div>
        </div>

        {/* Filtros: búsqueda + sede */}
        <div class="flex flex-wrap items-center gap-3">
          <div class="relative min-w-[220px] flex-1">
            <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              data-testid="people-search"
              value={search()}
              onInput={(e) => setSearch(e.currentTarget.value)}
              placeholder={t(msg`Search by name or ID…`)}
              class="w-full rounded-xl border-2 border-border bg-field-background py-2 pl-10 pr-3 text-sm focus-visible:border-primary focus-visible:outline-none"
            />
          </div>
          <div class="w-52">
            <Select
              options={campusOptions()}
              value={campusId()}
              onChange={setCampusId}
              aria-label={t(msg`Filter by campus`)}
            />
          </div>
        </div>

        {/* Pestañas */}
        <PillTabs
          tabs={tabItems()}
          value={tab()}
          onChange={setTab}
          testIdPrefix="people-tab"
          label={t(msg`People categories`)}
          trailing={(id) => (
            <span class="rounded-full bg-accent/12 px-1.5 text-xs font-bold text-accent">
              {countFor(id)}
            </span>
          )}
        />

        {/* Lista */}
        <Show
          when={!directory.isLoading}
          fallback={
            <div class="space-y-2">
              <Skeleton class="h-16 w-full rounded-xl" />
              <Skeleton class="h-16 w-full rounded-xl" />
              <Skeleton class="h-16 w-full rounded-xl" />
            </div>
          }
        >
          <Show
            when={rows().length > 0}
            fallback={
              <EmptyState
                icon={Users}
                title={t(msg`No people here yet`)}
                description={t(msg`Invite members or adjust your search and campus filters.`)}
              />
            }
          >
            <ul class="space-y-2" data-testid="people-list">
              <For each={rows()}>
                {(row, index) => <PersonRow row={row} entering={isEntering()} index={index()} />}
              </For>
            </ul>
          </Show>
        </Show>
      </div>

      <Show when={activeOrgId()}>
        {(orgId) => (
          <PeopleInviteDialog
            open={inviteOpen()}
            onOpenChange={setInviteOpen}
            organizationId={orgId()}
            campuses={campuses.data ?? []}
            defaultRole={inviteRoleForTab()}
            onInvited={() => void directory.refetch?.()}
          />
        )}
      </Show>
    </PageContainer>
  );
};

const PersonRow: Component<{ row: DirectoryRow; entering: boolean; index: number }> = (props) => {
  const { t } = useI18n();
  const initials = () => {
    const name = props.row.name ?? props.row.email ?? "?";
    return name.slice(0, 2).toUpperCase();
  };
  const entrance = () => listEntranceProps(props.entering, props.index);
  return (
    <li
      data-testid="person-row"
      data-user-id={props.row.userId ?? ""}
      class={entrance().class}
      style={entrance().style}
    >
      <Link
        to={`/academy/people/${props.row.userId ?? ""}`}
        class="flex items-center justify-between gap-3 rounded-xl border border-border p-3 transition-[color,background-color,border-color,transform] duration-150 ease-out hover:border-primary/50 hover:bg-muted/30 active:scale-[0.99]"
      >
        <div class="flex min-w-0 items-center gap-3">
          <Show
            when={props.row.image}
            fallback={
              <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/15 text-sm font-bold text-accent">
                {initials()}
              </div>
            }
          >
            {(img) => (
              <img src={img()} alt="" class="h-10 w-10 shrink-0 rounded-full object-cover" />
            )}
          </Show>
          <div class="min-w-0">
            <p class="truncate text-sm font-semibold">
              {props.row.name ?? props.row.email ?? props.row.userId}
            </p>
            <p class="truncate text-xs text-muted-foreground">
              <Show when={props.row.nationalId}>{(id) => <>{id()} · </>}</Show>
              {props.row.email}
            </p>
          </div>
        </div>
        <span class="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground">
          {props.row.role ?? ""}
        </span>
      </Link>
      {/* AUTONOMY: los profesores tienen editor de perfil público propio —
          antes era inalcanzable desde el directorio (solo URL directa). */}
      <Show when={props.row.role === "teacher" && props.row.userId}>
        {(uid) => (
          <Link
            to={`/academy/teachers/${uid()}`}
            class="mt-1 inline-flex items-center gap-1 pl-3 text-xs font-medium text-primary hover:underline"
            data-testid={`teacher-editor-link-${uid()}`}
          >
            {t(msg`Public teacher page →`)}
          </Link>
        )}
      </Show>
    </li>
  );
};
